const DEFAULT_FREE_PREFIX = "free-";

const configuredApiUrl = stripTrailingSlash(process.env.VUE_APP_API_URL || "");
const configuredLearnerAppUrl = stripTrailingSlash(process.env.VUE_APP_LEARNER_APP_CLIENT_URL || "");
const configuredFreeApiUrl = stripTrailingSlash(process.env.VUE_APP_FREE_API_URL || "");
const configuredFreeLearnerAppUrl = stripTrailingSlash(process.env.VUE_APP_FREE_LEARNER_APP_CLIENT_URL || "");
const configuredFreeDomains = parseCsv(process.env.VUE_APP_FREE_FRONTEND_DOMAINS || "");
const freePrefix = process.env.VUE_APP_FREE_PREFIX || DEFAULT_FREE_PREFIX;

// Per-region dashboard-api base URLs, keyed by the Directory Service's region
// code, e.g. "au=https://dashboard-api-au.reflectivelearning.co.za". The
// directory returns ONE apiUrl (the LEARNER api); the dashboard must map the
// region CODE to its OWN regional dashboard-api rather than reuse that url. The
// legacy/default region is omitted and falls back to VUE_APP_API_URL.
const configuredRegionApiUrls = parseRegionMap(process.env.VUE_APP_REGION_API_URLS || "");

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

const REGION_API_URL_KEY = "rl.region.apiUrl";
const REGION_EXPLICIT_KEY = "rl.region.explicit";
const REGION_REAUTH_KEY = "rl.region.reauth";

/**
 * Bump this to force every signed-in dashboard session through a fresh login once.
 *
 * Region routing is only ever applied inside userStore.login(). A session that
 * predates it has no region and nothing would ever give it one, so it keeps working
 * the default cluster indefinitely (Firebase auth persistence is LOCAL). Rather than
 * guess per-admin who needs moving, every session re-authenticates once and the
 * normal login path does the routing, which is the single authority for it.
 */
const REGION_REAUTH_VERSION = "region-routing-1";

function readCached(key: string): string {
  try {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

/**
 * Region-specific API base URL resolved by the Directory Service (or chosen via
 * the internal-admin region toggle). "" means use the default. Seeded from
 * localStorage so a returning session's first request hits the right cluster.
 */
let regionApiUrl: string = readCached(REGION_API_URL_KEY);

/** Listeners (e.g. the axios instance) re-read the base URL when the region changes. */
const regionChangeListeners: Array<() => void> = [];

export function registerRegionChangeListener(fn: () => void): void {
  regionChangeListeners.push(fn);
}

function notifyRegionChange(): void {
  regionChangeListeners.forEach(fn => {
    try {
      fn();
    } catch {
      /* ignore listener errors */
    }
  });
}

/**
 * Point the dashboard at a region-specific API. `explicit` marks a deliberate
 * choice (the internal-admin region toggle) that survives the per-login
 * internal-admin default reset; login-time auto-resolution passes explicit=false.
 */
export function setRegionApiUrl(url: string, options: { explicit?: boolean } = {}): void {
  regionApiUrl = url || "";
  try {
    if (typeof window !== "undefined") {
      if (regionApiUrl) {
        window.localStorage.setItem(REGION_API_URL_KEY, regionApiUrl);
      } else {
        window.localStorage.removeItem(REGION_API_URL_KEY);
      }
      if (options.explicit) {
        window.localStorage.setItem(REGION_EXPLICIT_KEY, "1");
      }
    }
  } catch {
    /* ignore storage errors */
  }
  notifyRegionChange();
}

export function clearRegionApiUrl(): void {
  regionApiUrl = "";
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(REGION_API_URL_KEY);
      window.localStorage.removeItem(REGION_EXPLICIT_KEY);
    }
  } catch {
    /* ignore storage errors */
  }
  notifyRegionChange();
}

/** Whether the current region was explicitly chosen (toggle) vs auto-resolved at login. */
export function hasExplicitRegion(): boolean {
  return readCached(REGION_EXPLICIT_KEY) === "1";
}

/**
 * Whether this browser still needs the one-time re-authentication described on
 * REGION_REAUTH_VERSION. False once markRegionReauthDone() has run.
 */
export function needsRegionReauth(): boolean {
  return readCached(REGION_REAUTH_KEY) !== REGION_REAUTH_VERSION;
}

/**
 * Record that this browser has completed the one-time re-authentication.
 *
 * Written AFTER the storage purge and BEFORE the redirect, so the marker is the one
 * thing that survives. That makes the forced logout un-repeatable by construction: it
 * cannot loop even if the subsequent login fails, is abandoned, or errors.
 */
export function markRegionReauthDone(): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(REGION_REAUTH_KEY, REGION_REAUTH_VERSION);
    }
  } catch {
    /* ignore storage errors */
  }
}

function parseCsv(value: string): string[] {
  return value
    .split(",")
    .map(entry => entry.trim().toLowerCase())
    .filter(Boolean);
}

/** Parse "au=https://…,us=https://…" into { au: "https://…", us: "https://…" }. */
function parseRegionMap(value: string): Record<string, string> {
  const map: Record<string, string> = {};
  value
    .split(",")
    .map(entry => entry.trim())
    .filter(Boolean)
    .forEach(entry => {
      const eq = entry.indexOf("=");
      if (eq <= 0) return;
      const code = entry.slice(0, eq).trim().toLowerCase();
      const url = stripTrailingSlash(entry.slice(eq + 1).trim());
      if (code && url) map[code] = url;
    });
  return map;
}

/**
 * This app's dashboard-api base URL for a Directory-resolved region code, or ""
 * for the legacy/default region (or an unmapped code) so the caller keeps the
 * default. Keyed on the region CODE, never on the directory's learner apiUrl.
 */
export function apiUrlForRegion(region: string): string {
  if (!region) return "";
  return configuredRegionApiUrls[region.toLowerCase()] || "";
}

/**
 * Every configured regional dashboard-api, excluding the default. Used when a
 * request carries no identity the Directory can resolve, so the caller has to
 * ask each cluster in turn (see userStore.getAdminAccount).
 */
export function getRegionalDashboardApiUrls(): string[] {
  return Object.values(configuredRegionApiUrls);
}

/** Parse "au=Australia,eu=Europe" into an ordered [{code,label}] list. */
function parseRegionOptions(value: string): Array<{ code: string; label: string }> {
  return value
    .split(",")
    .map(entry => entry.trim())
    .filter(Boolean)
    .map(entry => {
      const eq = entry.indexOf("=");
      if (eq <= 0) return null;
      const code = entry.slice(0, eq).trim().toLowerCase();
      const label = entry.slice(eq + 1).trim();
      return code && label ? { code, label } : null;
    })
    .filter((entry): entry is { code: string; label: string } => entry !== null);
}

const configuredRegionOptions = parseRegionOptions(process.env.VUE_APP_REGION_OPTIONS || "");

/**
 * Regions an admin may pick from the login-page selector (a fallback for shared
 * internal admins whom the Directory tie-break can't disambiguate). Config-driven
 * via VUE_APP_REGION_OPTIONS; empty (e.g. single-region deploys) hides the selector.
 * A picked region maps to its dashboard-api via apiUrlForRegion; a code with no
 * mapping (the legacy/default region) resolves to "" = the default VUE_APP_API_URL.
 */
export function getSelectableRegions(): Array<{ code: string; label: string }> {
  return configuredRegionOptions;
}

function getRuntimeHostname(): string {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.hostname.toLowerCase();
}

function shouldUseFreeUrls(hostname: string): boolean {
  if (!hostname) {
    return false;
  }

  if (configuredFreeDomains.includes(hostname)) {
    return true;
  }

  return hostname.startsWith(freePrefix.toLowerCase());
}

function deriveFreeUrl(url: string): string {
  if (!url) {
    return "";
  }

  try {
    const parsedUrl = new URL(url);
    const hostParts = parsedUrl.hostname.split(".");
    const primarySubdomain = hostParts.shift();

    if (!primarySubdomain || !hostParts.length || primarySubdomain.startsWith(freePrefix)) {
      return "";
    }

    parsedUrl.hostname = `${freePrefix}${primarySubdomain}.${hostParts.join(".")}`;
    return stripTrailingSlash(parsedUrl.toString());
  } catch {
    return "";
  }
}

function resolveUrl(configuredUrl: string, explicitFreeUrl: string): string {
  const hostname = getRuntimeHostname();

  if (!shouldUseFreeUrls(hostname)) {
    return configuredUrl;
  }

  return explicitFreeUrl || deriveFreeUrl(configuredUrl) || configuredUrl;
}

export function getDashboardApiUrl(): string {
  // A region resolved by the Directory Service (or chosen via the internal-admin
  // toggle) wins over the default, but MUST still pass through the zero-rating
  // (free-*) rewrite — the directory returns an apiUrl for every user, so
  // returning it raw would drop free-host traffic off the whitelisted proxy.
  return resolveUrl(regionApiUrl || configuredApiUrl, configuredFreeApiUrl);
}

/**
 * The dashboard-api the app would use with NO region applied: the default
 * (legacy/EU) cluster, still passed through the zero-rating rewrite.
 *
 * Internal admins work the default dataset, so login probes this URL to decide
 * whether it can move them off the region the Directory resolved them to.
 * See userStore.login().
 */
export function getDefaultDashboardApiUrl(): string {
  return resolveUrl(configuredApiUrl, configuredFreeApiUrl);
}

/** True when a region is currently applied, i.e. we are NOT on the default cluster. */
export function hasRegionApiUrl(): boolean {
  return Boolean(regionApiUrl);
}

export function getLearnerAppUrl(): string {
  return resolveUrl(configuredLearnerAppUrl, configuredFreeLearnerAppUrl);
}
